import Button from "@material-ui/core/Button/Button";
import Checkbox from "@material-ui/core/Checkbox/Checkbox";
import FormControl from "@material-ui/core/FormControl/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup/FormGroup";
import FormLabel from "@material-ui/core/FormLabel/FormLabel";
import Grid from "@material-ui/core/Grid/Grid";
import LinearProgress from "@material-ui/core/LinearProgress/LinearProgress";
import MenuItem from "@material-ui/core/MenuItem/MenuItem";
import Select from "@material-ui/core/Select/Select";
import Typography from "@material-ui/core/Typography/Typography";
import Person from "@material-ui/icons/Person";
import * as React from "react";
import Dropzone from "react-dropzone";
import { DebateNoteSubType, DebateNoteSubType_DISPLAY_NAMES } from "../../model/autogen/DebateNoteSubType";
import { DebateNotesRequestFactory } from "../../model/autogen/DebateNotesController";
import { DebateNotesModel } from "../../model/autogen/DebateNotesModel";
import { FilesRequestFactory } from "../../model/autogen/FilesController";
import { IdNamePair } from "../../model/autogen/IdNamePair";
import { UserLevel } from "../../model/autogen/UserLevel";
import { downloadLinkResolver } from "../../pages/brief-item-page";
import { withEditorPage } from "../../pages/editor-page";
import { WithDefaultStyles, withDefaultStyles } from "../../utils/createStyles";
import { strIsNullOrEmpty } from "../../utils/string";
import DEFAULT_DROPZONE_STYLE from "../dropzone-style";
import { FileAttachmentList } from "../file-attachment-list";
import { IContentForm } from "../forms/icontent-form";
import { LinkCollectionEditor } from "../link-collection-editor";
import { IAuthContextProps, withAuthContext } from "../with-auth-context";
import { ISnackbarContextProps, withSnackbar } from "../with-snackbar";
import { FormStateHelperComponent, ICreateTags, IDocumentEditModelState } from "./form-state-helper";
import Paper from "@material-ui/core/Paper/Paper";
import { $enum } from "ts-enum-util";
import { getPartyPositions, getBillStages } from "../../model/enum-display";
import { getDisplayLabels } from "../display/debate-notes-display";
import { PeopleLookupDialog } from "./people-lookup-dialog";
import { isArrayNullOrEmpty } from "../../utils/array";

export interface IDebateNoteFormComponentProps extends IContentForm<DebateNotesModel> {

}

export interface IDebateNoteFormComponentState extends DebateNotesModel {
    isUploadingFiles: boolean;
    isPickingPerson: boolean;
    acknowledgedNoSpeeches: boolean;
    acknowledgedNoLinks: boolean;
}

type DebateNoteFormComponentProps = IDebateNoteFormComponentProps & IAuthContextProps & ISnackbarContextProps & WithDefaultStyles;

export class DebateNoteFormComponent extends React.Component<DebateNoteFormComponentProps, IDebateNoteFormComponentState> {
    private counter: number;

    constructor(props: DebateNoteFormComponentProps) {
        super(props);
        this.counter = 0;
        this.state = { ...props.item, isUploadingFiles: false, isPickingPerson: false, acknowledgedNoSpeeches: false, acknowledgedNoLinks: false };
    }
    private onChange = (key: keyof IDebateNoteFormComponentState, val: any) =>
        this.setState({ [key]: val } as any);
    private onLinkChanged = (index: number, title: string, url: string) => {
        const { links } = this.state;
        if (links[index]) {
            links[index].title = title;
            links[index].url = url;
            this.setState({ links });
        }
    }
    private onAddLink = () => {
        const { links } = this.state;
        links.push({
            id: (this.counter--),
            title: "New Link",
            icon: "web",
            url: "https://"
        });
        this.setState({ links });
    }
    private onRemoveLink = (index: number) => {
        const { links } = this.state;
        links.splice(index, 1);
        this.setState({ links });
    }
    private onFileDrop = async (acceptedFiles: File[], rejectedFiles: File[]) => {
        const { callApi } = this.props;
        try {
            if (!isArrayNullOrEmpty(acceptedFiles)) {
                this.setState({ isUploadingFiles: true });
                const result = await callApi(f => FilesRequestFactory.upload(f, { file: acceptedFiles[0] }));
                const { assignFiles } = this.state;
                this.setState({ isUploadingFiles: false, assignFiles: [...(assignFiles || []), result] });
            }
        } catch (e) {
            this.props.showSnackbarMessage(`Failed to upload file: ${e}`);
            this.setState({ isUploadingFiles: false });
        }
    }
    private onSubTypeChange = (e: any) => {
        this.setState({ subType: parseInt(e.target.value, 10) });
    }
    private onBillStageChanged = (e: any) => {
        this.setState({ billStage: parseInt(e.target.value, 10) });
    }
    private onPartyPositionChanged = (e: any) => {
        this.setState({ partyPosition: parseInt(e.target.value, 10) });
    }
    private onPrepareForSave = (localState: IDocumentEditModelState & ICreateTags) => {
        return {
            ...localState,
            ...this.state
        }
    }
    private onPickPerson = (e: any) => {
        this.setState({ isPickingPerson: true });
    }
    private onPersonSelected = (person: IdNamePair) => {
        this.setState({ minister: { ...person }, isPickingPerson: false });
    }
    private onCancelPickingPerson = (e: any) => {
        this.setState({ isPickingPerson: false });
    }
    public render(): JSX.Element {
        const { title, summary, files, isUploadingFiles, assignFiles, subType, partyPosition, billStage, minister, isPickingPerson } = this.state;
        const { isEditing, classes, item } = this.props;
        const hasAtLeastOneFileUploaded = !isArrayNullOrEmpty(assignFiles) || !isArrayNullOrEmpty(files);
        let passesSpeech = this.state.acknowledgedNoSpeeches || hasAtLeastOneFileUploaded;
        let passesLinks = this.state.acknowledgedNoLinks || !isArrayNullOrEmpty(this.state.links);
        if (subType == DebateNoteSubType.MembersBill) {
            passesSpeech = true;
        } else if (subType != DebateNoteSubType.GovernmentBill) {
            passesSpeech = true;
            passesLinks = true;
        }
        const cannotSave = strIsNullOrEmpty(title) || !passesLinks || !passesSpeech;
        const {
            ministerLabel,
            summaryLabel,
            existingSpeechesLabel,
            bodyLabel,
            billStageLabel,
            partyPositionLabel,
            speechesLabel,
            linksLabel
        } = getDisplayLabels(subType);

        const subTypeEnum = $enum(DebateNoteSubType);
        const billStageValues = getBillStages();
        const partyPositionValues = getPartyPositions();
        const submitAction = (isEditing === true) ? DebateNotesRequestFactory.update : DebateNotesRequestFactory.create;

        return <FormStateHelperComponent
            item={item}
            documentItemTypeName={bodyLabel}
            documentItemTypeNamePlural="Body Items"
            documentTypeName="Debate Notes"
            isEditing={this.props.isEditing}
            submitAction={submitAction}
            cannotSave={cannotSave}
            onPrepareForSave={this.onPrepareForSave}
            onSaveCompleted={this.props.onSaveCompleted}
            initialTags={this.state.tags}
            onChange={this.onChange}>{({ DocumentItemListEditor, DocumentTagListEditor, DocumentFormSubmitButton, DocumentTextField, DocumentRTEField }: any) => (
                <div className={classes.root}>
                    <Typography className={this.props.classes.input} variant="h5">{(isEditing === true) ? "Edit" : "New"} Debate Note</Typography>
                    <FormControl>
                        <FormLabel>Sub-Type</FormLabel>
                        {/* In edit mode, we're locking down Sub-Type selection (for simplicity) */}
                        {(() => {
                            if (isEditing === true) {
                                return <p><strong>{DebateNoteSubType_DISPLAY_NAMES[subType]}</strong></p>
                            } else {
                                return <Select value={subType} onChange={this.onSubTypeChange}>
                                    {subTypeEnum.map(a => <MenuItem key={a} value={a}>{DebateNoteSubType_DISPLAY_NAMES[a]}</MenuItem>)}
                                </Select>
                            }
                        })()}
                    </FormControl>
                    <DocumentTextField label="Title" name="title" value={title} extraProps={{ rows: 2, rowsMax: 4 }} />
                    <FormControl>
                        <FormLabel>{ministerLabel}</FormLabel>
                        <p>{minister && <><Person /> {minister.name || `Person with id (${minister.id})`}</>}<Button variant="contained" color="primary" onClick={this.onPickPerson}>Change</Button></p>
                        <PeopleLookupDialog isPickingPerson={isPickingPerson}
                                            personLabel={ministerLabel}
                                            onPersonSelected={this.onPersonSelected}
                                            onCancelPickingPerson={this.onCancelPickingPerson} />
                    </FormControl>
                    {(subType == DebateNoteSubType.GovernmentBill || subType == DebateNoteSubType.MembersBill) && <FormControl>
                        <FormLabel>{billStageLabel}</FormLabel>
                        <Select value={billStage} onChange={this.onBillStageChanged}>
                            {billStageValues.map(a => <MenuItem key={a[0]} value={a[0]}>{a[1]}</MenuItem>)}
                        </Select>
                    </FormControl>}
                    {(subType == DebateNoteSubType.MembersBill) && <FormControl>
                        <FormLabel>{partyPositionLabel}</FormLabel>
                        <Select value={partyPosition} onChange={this.onPartyPositionChanged}>
                            {partyPositionValues.map(a => <MenuItem key={a[0]} value={a[0]}>{a[1]}</MenuItem>)}
                        </Select>
                    </FormControl>}
                    <DocumentRTEField label={summaryLabel} name="summary" value={summary} />
                    <DocumentItemListEditor />
                    {(subType == DebateNoteSubType.GovernmentBill || subType == DebateNoteSubType.MembersBill) && files && files.length > 0 && (<FormControl>
                        <FormLabel>{existingSpeechesLabel}</FormLabel>
                        <FileAttachmentList files={files} downloadLinkResolver={downloadLinkResolver} editing={true} />
                    </FormControl>)}
                    {(subType == DebateNoteSubType.GovernmentBill || subType == DebateNoteSubType.MembersBill) && <FormControl>
                        <FormLabel>{speechesLabel}</FormLabel>
                        {!hasAtLeastOneFileUploaded && this.state.acknowledgedNoSpeeches === false && <Paper><Typography>You need to upload at least one file, or tick <strong>There are no {speechesLabel} for this debate</strong> to indicate there are no files to be submitted.</Typography></Paper>}
                        {!hasAtLeastOneFileUploaded && <FormControlLabel control={<Checkbox checked={this.state.acknowledgedNoSpeeches} onChange={e => this.setState({ acknowledgedNoSpeeches: e.target.checked })} />} label={`There are no Documents for this debate`} />}
                        {this.state.acknowledgedNoSpeeches === false && <Dropzone multiple={false} onDrop={this.onFileDrop} style={DEFAULT_DROPZONE_STYLE.default} disabledStyle={DEFAULT_DROPZONE_STYLE.disabled} activeStyle={DEFAULT_DROPZONE_STYLE.active} acceptStyle={DEFAULT_DROPZONE_STYLE.active} rejectStyle={DEFAULT_DROPZONE_STYLE.rejected}>
                            <Grid justify="center" container spacing={24}>
                                <Grid item xs={6}>
                                    {(() => {
                                        if (!isArrayNullOrEmpty(assignFiles)) {
                                            return <>
                                                <div>
                                                    {isUploadingFiles && <LinearProgress />}
                                                    <p style={DEFAULT_DROPZONE_STYLE.text}>Speech files to be added ({assignFiles.length})</p>
                                                    <ul>{assignFiles.map(f => <li key={f.id} style={DEFAULT_DROPZONE_STYLE.listItem}>{f.fileName}</li>)}</ul>
                                                </div>
                                            </>;
                                        } else {
                                            return <>
                                                <div>
                                                    {isUploadingFiles && <LinearProgress />}
                                                    <p style={DEFAULT_DROPZONE_STYLE.text}>Click here or drag/drop a speech file here to upload it</p>
                                                </div>
                                            </>;
                                        }
                                    })()}
                                </Grid>
                            </Grid>
                        </Dropzone>}
                    </FormControl>}
                    <FormGroup>
                        <Typography variant="h6">{linksLabel}</Typography>
                        {isArrayNullOrEmpty(this.state.links) && this.state.acknowledgedNoLinks === false && <Paper><Typography>You need to specify at least one link or tick <strong>There are no {linksLabel} for this debate</strong> to indicate there are no links to be submitted.</Typography></Paper>}
                        {this.state.acknowledgedNoLinks === false && <LinkCollectionEditor items={this.state.links} onAdd={this.onAddLink} onChange={this.onLinkChanged} onRemove={this.onRemoveLink} />}
                        {isArrayNullOrEmpty(this.state.links) && <FormControlLabel control={<Checkbox checked={this.state.acknowledgedNoLinks} onChange={e => this.setState({ acknowledgedNoLinks: e.target.checked })} />} label={`There are no ${linksLabel} for this debate`} />}
                    </FormGroup>
                    <DocumentTagListEditor />
                    <DocumentFormSubmitButton />
                </div>
            )}</FormStateHelperComponent>;
    }
}

// HACK: wrapper to workaround the fact that our current codegen doesn't support argument default values, so there's a signature mismatch
const debateNotesGet = (fetcher: (url: string, init: RequestInit) => Promise<Response>, id: number) => DebateNotesRequestFactory.getById(fetcher, id, false);

const DebateNoteFormStyled = withDefaultStyles(DebateNoteFormComponent) as React.ComponentType<DebateNoteFormComponentProps>;

export const DebateNoteForm = withSnackbar(withAuthContext(DebateNoteFormStyled)) as React.ComponentType<IDebateNoteFormComponentProps>;

export const DebateNoteEditForm = withAuthContext(withEditorPage(DebateNoteForm as any, debateNotesGet), UserLevel.SystemUser) as React.ComponentType<IDebateNoteFormComponentProps>;


// WEBPACK FOOTER //
// ./src/components/forms/debate-note-form.tsx